import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Calendar } from 'lucide-react';
import { getBlog } from '@/lib/api';

const Blog = ({ lang, t }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getBlog(lang)
      .then((data) => { if (active) setPosts(data.items || []); })
      .catch(() => { if (active) setPosts([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [lang]);

  return (
    <section id="blog" className="relative py-20 sm:py-24 lg:py-28" data-testid="blog">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="kicker-mono mb-3">{t.blog.kicker}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-[650] tracking-tight text-white" data-testid="blog-title">{t.blog.title}</h2>
            <p className="mt-3 text-white/70 max-w-xl">{t.blog.subtitle}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {loading && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-[rgb(var(--bg-1))] h-80 animate-pulse" data-testid={`blog-skeleton-${i}`} />
          ))}
          {!loading && posts.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              whileHover={{ y: -4 }}
              className="group rounded-2xl overflow-hidden border border-white/10 bg-[rgb(var(--bg-1))] hover:border-[rgba(5,126,243,.5)] transition-colors"
              data-testid={`blog-card-${i}`}
            >
              <Link to={`/blog/${p.slug}`} className="block" data-testid={`blog-card-link-${i}`}>
                <div className="relative h-44 overflow-hidden bg-[rgb(var(--bg-2))]">
                  {p.cover && (
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition duration-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--bg-1))] via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 chip">
                    <span className="dot" />{p.category}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[11px] font-mono-accent uppercase tracking-wider text-white/50">
                    <Calendar size={12} /> {p.date}
                  </div>
                  <h3 className="mt-2 font-display text-lg font-[650] text-white leading-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <span
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[rgb(var(--brand-electric))] group-hover:text-white transition-colors"
                    data-testid={`blog-read-more-${i}`}
                  >
                    {t.blog.read_more} <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
